import { createAsyncThunk } from "@reduxjs/toolkit";
import config from "../../config";
import axiosClient from "../../common/api/request";

export const getShopSettings = createAsyncThunk(
  "get_shop_settings",
  async (params, { rejectWithValue }) => {
    try {
      const data = await axiosClient.get(
        `${config.API_URL}/shop_settings/`,
        { params },
        config
      );
      return data;
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        // [CANA-561] the status lets the tab tell a 403 (no view_shopsetting)
        // apart from a validation body.
        return rejectWithValue({
          ...error.response.data,
          status: error.response.status,
        });
      } else {
        return rejectWithValue({
          Error: error.message,
        });
      }
    }
  }
);

export const patchShopSetting = createAsyncThunk(
  "patch_shop_setting",
  async (params, { rejectWithValue }) => {
    try {
      const data = await axiosClient.patch(
        `${config.API_URL}/shop_settings/${params?.id}/`,
        params,
        config
      );
      return data;
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        return rejectWithValue(error.response.data);
      } else {
        return rejectWithValue({
          Error: error.message,
        });
      }
    }
  }
);

// [CANA-547] The editable theme keys with defaults, labels, groups and tiers.
// The Theme Editor renders its form from this instead of carrying its own
// field list. It changes only on deploy, so one fetch per session is enough.
export const getShopThemeSchema = createAsyncThunk(
  "get_shop_theme_schema",
  async (_, { rejectWithValue }) => {
    try {
      const data = await axiosClient.get(
        `${config.API_URL}/shop_settings/theme_schema/`,
        {},
        config
      );
      return data;
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
      ) {
        return rejectWithValue(error.response.data);
      } else {
        return rejectWithValue({
          Error: error.message,
        });
      }
    }
  }
);

// [CANA-555] AI colors for the Theme Editor (backend CANA-554). The rejected
// payload is the raw body plus the HTTP status: a 429 carries the server
// quota, a 502 carries a message and the quota, a 503 means the feature is
// off on that environment.
const themeAiReject = (error, rejectWithValue) => {
  const body =
    error.response && error.response.data && typeof error.response.data === "object"
      ? error.response.data
      : { Error: error.message };
  return rejectWithValue({ ...body, status: error.response?.status ?? null });
};

const THEME_AI_URL = () => `${config.API_URL}/shop_settings/theme_ai`;

export const getThemeAiPalettes = createAsyncThunk(
  "get_theme_ai_palettes",
  async (_, { rejectWithValue }) => {
    try {
      return await axiosClient.get(`${THEME_AI_URL()}/palettes/`, {}, config);
    } catch (error) {
      return themeAiReject(error, rejectWithValue);
    }
  }
);

export const generateThemeAiPalettes = createAsyncThunk(
  "generate_theme_ai_palettes",
  async (_, { rejectWithValue }) => {
    try {
      return await axiosClient.post(`${THEME_AI_URL()}/palettes/`, {}, config);
    } catch (error) {
      return themeAiReject(error, rejectWithValue);
    }
  }
);

export const getThemeAiChat = createAsyncThunk(
  "get_theme_ai_chat",
  async (params, { rejectWithValue }) => {
    try {
      return await axiosClient.get(
        `${THEME_AI_URL()}/chat/`,
        { params: params?.before ? { before: params.before } : {} },
        config
      );
    } catch (error) {
      return themeAiReject(error, rejectWithValue);
    }
  }
);

export const sendThemeAiMessage = createAsyncThunk(
  "send_theme_ai_message",
  async ({ message }, { rejectWithValue }) => {
    try {
      return await axiosClient.post(
        `${THEME_AI_URL()}/chat/`,
        { message },
        config
      );
    } catch (error) {
      return themeAiReject(error, rejectWithValue);
    }
  }
);

export const clearThemeAiChat = createAsyncThunk(
  "clear_theme_ai_chat",
  async (_, { rejectWithValue }) => {
    try {
      await axiosClient.delete(`${THEME_AI_URL()}/chat/`, config);
      return true;
    } catch (error) {
      return themeAiReject(error, rejectWithValue);
    }
  }
);

export const getThemeAiPrompt = createAsyncThunk(
  "get_theme_ai_prompt",
  async (_, { rejectWithValue }) => {
    try {
      return await axiosClient.get(`${THEME_AI_URL()}/prompt/`, {}, config);
    } catch (error) {
      return themeAiReject(error, rejectWithValue);
    }
  }
);
